import { FileIcon } from "@phosphor-icons/react/dist/csr/File";
import { FileImageIcon } from "@phosphor-icons/react/dist/csr/FileImage";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { FileXlsIcon } from "@phosphor-icons/react/dist/csr/FileXls";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { DOCUMENT_STATUS } from "./document-constants";
import { MetaList } from "./meta-list";
import type { ProfitabilityDocument } from "./types";

export const formatByteSize = (bytes: number | null) => {
  if (bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
/** The file-type icon for a file name: a sheet, an image, a text file. */
export const documentIcon = (name: string) =>
  /\.(xlsx|xlsm|csv|tsv)$/i.test(name)
    ? FileXlsIcon
    : /\.(png|jpe?g|gif|webp|tiff?)$/i.test(name)
      ? FileImageIcon
      : /\.(pdf|docx|pptx|txt|md|json)$/i.test(name)
        ? FileTextIcon
        : FileIcon;

// Read errors are written as clauses ("payroll.xlsx has no rows"); the
// follow-up sentence needs them closed.
const asSentence = (text: string) =>
  /[.!?]$/.test(text.trim()) ? text : `${text.trim()}.`;

export const DocumentChip = ({
  document,
  onRemove,
}: {
  document: ProfitabilityDocument;
  onRemove?: () => void;
}) => {
  const Icon = documentIcon(document.file_name);
  const reading = document.status === DOCUMENT_STATUS.pending;
  const failed = document.status === DOCUMENT_STATUS.failed;
  const rows = document.sheets.reduce((sum, sheet) => sum + sheet.row_count, 0);
  const tables = document.sheets.length;
  const summary =
    document.kind === "text"
      ? ["Read as text"]
      : [
          `${tables} ${tables === 1 ? "table" : "tables"}`,
          `${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"}`,
        ];
  return (
    <Stack
      direction="row"
      align="start"
      gap={3}
      data-testid="report-document"
      className="max-w-full min-w-0 rounded-lg border border-line bg-surface-raised p-3"
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-subtle" />
      <Stack gap={1} className="min-w-0 flex-1">
        <Text variant="label" className="[overflow-wrap:anywhere]">
          {document.file_name}
        </Text>
        <Text variant="caption" tone="muted">
          <MetaList>
            {formatByteSize(document.byte_size)}
            {reading ? "Reading…" : failed ? "Couldn't read" : summary}
          </MetaList>
        </Text>
        {failed && (
          <Text tone="destructive">
            {asSentence(document.error ?? "This file could not be read.")}{" "}
            Remove it and add a readable copy.
          </Text>
        )}
      </Stack>
      {onRemove && (
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Remove ${document.file_name}`}
          onClick={onRemove}
        >
          <XIcon aria-hidden />
        </Button>
      )}
    </Stack>
  );
};
